import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import {
  Button,
  Card,
  Input,
  Modal,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from 'keep-react'
import { Pencil, Trash } from 'phosphor-react'

import { Wrapper } from 'components'
import { RouteNamesAuth } from 'routes/enum'
import { PageType } from 'api/types/page'
import { pageService } from 'api/services/page'
import { sanitizeSlug } from 'utils/sanitizeString'

// form validation schema
const validationSchema = Yup.object().shape({
  title: Yup.string().required('Obrigatório'),
})

export const BackofficePages = () => {
  const navigate = useNavigate()

  //first load and is api being called
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isApiBeingCalled, setIsApiBeingCalled] = useState<boolean>(false)

  // track selected page for deletion
  const [selected, setSelected] = useState<number | null>(null)

  // pages data
  const [data, setData] = useState<PageType[] | null>(null)

  //modals visibility
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false)

  // fetch campaign list from API
  const getPageList = async () => {
    setIsLoading(true)
    try {
      const response = await pageService.getPageList()
      setData(response as PageType[])
      setIsLoading(false)
    } catch (error) {
      toast.error('Não foi possível carregar as campanhas.')
    }
  }

  // delete selected product
  const deletePage = async () => {
    if (!selected) return
    try {
      setIsApiBeingCalled(true)
      await pageService.deletePage({
        id: selected,
      })
      setShowDeleteModal(false)
      getPageList()
      setIsApiBeingCalled(false)
      toast.success('Página apagada com sucesso.')
    } catch (error) {
      toast.error('Não foi possível apagar a página.')
    }
  }

  //formik to create a page
  const formik = useFormik({
    initialValues: {
      title: '',
    },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema,
    onSubmit: async values => {
      setIsApiBeingCalled(true)

      try {
        const newPage: PageType = {
          title: values.title,
          slug: sanitizeSlug(values.title),
          content: '',
          active: false,
        } as PageType

        const response = await pageService.savePage(newPage)

        try {
          setShowCreateModal(false)
          setIsApiBeingCalled(false)
          navigate(
            RouteNamesAuth.BACKOFFICE_PAGE_DETAILS.replace(':id', response),
          )
        } catch (error) {
          console.error('Error parsing page ID:', error)
        }

        toast.success('Página criada com sucesso.')
      } catch (error) {
        toast.error('Não foi possível criar a página.')
      }
    },
  })

  // initial data fetch
  useEffect(() => {
    getPageList()
  }, [])

  return (
    <div className="w-full flex-1 py-12 overflow-auto">
      <Wrapper>
        <h1 className="pb-10 text-xxxl font-medium text-primary-600 flex items-center justify-between">
          <span>Páginas</span>
          <Button
            variant="default"
            color="secondary"
            onClick={() => {
              setShowCreateModal(true)
            }}
          >
            Adicionar Nova Página
          </Button>
        </h1>

        {isLoading ? (
          <Card className="p-4 w-full max-w-full">
            <Spinner color="primary" className="mx-auto" />
          </Card>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Página</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data &&
                  data.map((page: PageType, index: number) => (
                    <TableRow
                      key={`${page.id}-${index}`}
                      className="even:bg-primary-50/25"
                    >
                      <TableCell className="normal-case">
                        {page.title}
                      </TableCell>
                      <TableCell className="normal-case">{page.slug}</TableCell>

                      <TableCell>
                        <div className="flex items-center gap-2 justify-end">
                          <Button
                            variant="default"
                            color="secondary"
                            onClick={() => {
                              navigate(
                                RouteNamesAuth.BACKOFFICE_PAGE_DETAILS.replace(
                                  ':id',
                                  page.id!.toString(),
                                ),
                              )
                            }}
                          >
                            <Pencil size={16} />
                          </Button>
                          <Button
                            variant="default"
                            color="error"
                            disabled={isApiBeingCalled}
                            onClick={() => {
                              setSelected(page.id!)
                              setShowDeleteModal(true)
                            }}
                          >
                            <Trash size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </>
        )}
      </Wrapper>

      <Modal open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <ModalContent>
          <ModalHeader className="mb-6 space-y-3">
            <ModalTitle>Apagar Página</ModalTitle>
            <ModalDescription>
              Quer apagar esta página?
              <br />
              <br />
              <strong className="text-error-500">
                Todos os dados associados a esta página serão perdidos.
              </strong>
            </ModalDescription>
          </ModalHeader>
          <ModalFooter className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              color="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" onClick={() => deletePage()}>
              Apagar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal open={showCreateModal} onOpenChange={setShowCreateModal}>
        <ModalContent>
          <ModalHeader className="mb-6 space-y-3">
            <ModalTitle>Criar Página</ModalTitle>
            <ModalDescription>
              <div className="space-y-4">
                <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                  <h3 className="text-body-4 font-bold text-metal-900">
                    Título
                  </h3>
                  <div>
                    <Input
                      name="title"
                      value={formik.values.title}
                      onChange={formik.handleChange}
                    />
                  </div>
                </div>
              </div>
              <br />
              <br />
            </ModalDescription>
          </ModalHeader>
          <ModalFooter className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              color="secondary"
              onClick={() => setShowCreateModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              color="secondary"
              onClick={() => formik.handleSubmit()}
            >
              Criar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}
